import { useState } from "react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  Send,
  CheckCircle,
  Loader2,
  Shield,
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    full_name: "",
    phone: "",
    email: "",
    property_size: "",
    service_needed: "",
    preferred_date: "",
    message: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !formData.full_name ||
      !formData.phone ||
      !formData.email ||
      !formData.property_size ||
      !formData.service_needed
    ) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/leads`, formData);
      setIsSubmitted(true);
      toast.success("Thank you! We'll contact you soon.");
      setFormData({
        full_name: "",
        phone: "",
        email: "",
        property_size: "",
        service_needed: "",
        preferred_date: "",
        message: "",
      });
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
      console.error("Lead submission error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const propertySizes = [
    "Under 1,000 sq ft",
    "1,000 - 2,500 sq ft",
    "2,500 - 5,000 sq ft",
    "5,000 - 10,000 sq ft",
    "Over 10,000 sq ft",
  ];

  const services = [
    "Landscape Design",
    "Lawn Installation",
    "Garden Planting",
    "Lawn Mowing & Maintenance",
    "Hedge & Shrub Trimming",
    "Seasonal Cleanup",
    "Irrigation Setup",
    "Mulching & Soil Prep",
    "Full Property Assessment",
  ];

  return (
    <div data-testid="contact-page" className="min-h-screen bg-[#FAFAF8]">
      <Header />
      <main>
        {/* Hero */}
        <section className="bg-[#2D5A27] py-20 md:py-28">
          <div className="container mx-auto max-w-7xl px-4 md:px-6 lg:px-8">
            <div className="max-w-3xl">
              <h1
                data-testid="contact-page-headline"
                className="font-heading text-4xl md:text-5xl lg:text-6xl text-white font-bold mb-6"
              >
                Contact Us
              </h1>
              <p className="text-white/80 text-lg md:text-xl leading-relaxed">
                Ready to transform your outdoor space? Get in touch for a free estimate. We'd love to hear from you.
              </p>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto max-w-7xl px-4 md:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
              {/* Contact Info */}
              <div>
                <h2 className="font-heading text-2xl md:text-3xl text-[#1A2C22] font-bold mb-8">
                  Get In Touch
                </h2>
                <div className="space-y-6 mb-10">
                  <div className="flex items-start gap-4 p-4 bg-white rounded-xl">
                    <div className="w-12 h-12 bg-[#F3F4F1] rounded-xl flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-[#2D5A27]" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#1A2C22]">Our Location</p>
                      <p className="text-[#4B5563]">
                        CasaCurate Store<br />
                        1513 Sunset Boulevard<br />
                        Los Angeles, CA 90027
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-4 bg-white rounded-xl">
                    <div className="w-12 h-12 bg-[#F3F4F1] rounded-xl flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-[#2D5A27]" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#1A2C22]">Phone</p>
                      <a
                        href="tel:+17711871113"
                        data-testid="contact-phone"
                        className="text-[#4B5563] hover:text-[#2D5A27] transition-colors"
                      >
                        +1-771-1871-113
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-4 bg-white rounded-xl">
                    <div className="w-12 h-12 bg-[#F3F4F1] rounded-xl flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-[#2D5A27]" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#1A2C22]">Email</p>
                      <a
                        href="mailto:info@casacurate.store"
                        data-testid="contact-email"
                        className="text-[#4B5563] hover:text-[#2D5A27] transition-colors"
                      >
                        info@casacurate.store
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-4 bg-white rounded-xl">
                    <div className="w-12 h-12 bg-[#F3F4F1] rounded-xl flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-[#2D5A27]" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#1A2C22]">Business Hours</p>
                      <p className="text-[#4B5563]">
                        Mon - Fri: 7:00 AM - 6:00 PM<br />
                        Sat: 8:00 AM - 4:00 PM<br />
                        Sun: Closed
                      </p>
                    </div>
                  </div>
                </div>

                {/* Privacy Note */}
                <div className="flex items-start gap-3 p-4 bg-[#F3F4F1] rounded-xl">
                  <Shield className="w-5 h-5 text-[#2D5A27] flex-shrink-0 mt-0.5" />
                  <p className="text-[#6B7280] text-sm">
                    We respect your privacy. Your information will only be used to respond to your request.
                  </p>
                </div>
              </div>

              {/* Form */}
              <div className="bg-white rounded-3xl p-8 lg:p-10 shadow-sm">
                {isSubmitted ? (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 bg-[#2D5A27] rounded-full flex items-center justify-center mx-auto mb-6">
                      <CheckCircle className="w-10 h-10 text-white" />
                    </div>
                    <h3 className="font-heading text-2xl text-[#1A2C22] font-bold mb-4">
                      Thank You!
                    </h3>
                    <p className="text-[#4B5563] mb-8">
                      We've received your request. A member of our team will contact you within 24 hours.
                    </p>
                    <Button
                      data-testid="submit-another-contact"
                      onClick={() => setIsSubmitted(false)}
                      className="bg-[#2D5A27] hover:bg-[#1E3F1B] text-white h-12 px-8 rounded-full"
                    >
                      Submit Another Request
                    </Button>
                  </div>
                ) : (
                  <>
                    <h3 className="font-heading text-2xl text-[#1A2C22] font-bold mb-6">
                      Request Free Estimate
                    </h3>
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div>
                          <Label htmlFor="full_name" className="text-[#1A2C22] font-medium">
                            Full Name *
                          </Label>
                          <Input
                            id="full_name"
                            name="full_name"
                            data-testid="contact-input-name"
                            value={formData.full_name}
                            onChange={handleInputChange}
                            placeholder="John Smith"
                            className="mt-2 h-12 bg-[#FAFAF8] border-gray-200"
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="phone" className="text-[#1A2C22] font-medium">
                            Phone Number *
                          </Label>
                          <Input
                            id="phone"
                            name="phone"
                            data-testid="contact-input-phone"
                            type="tel"
                            value={formData.phone}
                            onChange={handleInputChange}
                            placeholder="(555) 123-4567"
                            className="mt-2 h-12 bg-[#FAFAF8] border-gray-200"
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="email" className="text-[#1A2C22] font-medium">
                          Email Address *
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          data-testid="contact-input-email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="john@example.com"
                          className="mt-2 h-12 bg-[#FAFAF8] border-gray-200"
                          required
                        />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div>
                          <Label htmlFor="property_size" className="text-[#1A2C22] font-medium">
                            Property Size *
                          </Label>
                          <Select
                            value={formData.property_size}
                            onValueChange={(value) => handleSelectChange("property_size", value)}
                          >
                            <SelectTrigger
                              data-testid="contact-select-size"
                              className="mt-2 h-12 bg-[#FAFAF8] border-gray-200"
                            >
                              <SelectValue placeholder="Select size" />
                            </SelectTrigger>
                            <SelectContent>
                              {propertySizes.map((size) => (
                                <SelectItem key={size} value={size}>
                                  {size}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label htmlFor="service_needed" className="text-[#1A2C22] font-medium">
                            Service Needed *
                          </Label>
                          <Select
                            value={formData.service_needed}
                            onValueChange={(value) => handleSelectChange("service_needed", value)}
                          >
                            <SelectTrigger
                              data-testid="contact-select-service"
                              className="mt-2 h-12 bg-[#FAFAF8] border-gray-200"
                            >
                              <SelectValue placeholder="Select service" />
                            </SelectTrigger>
                            <SelectContent>
                              {services.map((service) => (
                                <SelectItem key={service} value={service}>
                                  {service}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="preferred_date" className="text-[#1A2C22] font-medium">
                          Preferred Service Date
                        </Label>
                        <Input
                          id="preferred_date"
                          name="preferred_date"
                          data-testid="contact-input-date"
                          type="date"
                          value={formData.preferred_date}
                          onChange={handleInputChange}
                          className="mt-2 h-12 bg-[#FAFAF8] border-gray-200"
                        />
                      </div>

                      <div>
                        <Label htmlFor="message" className="text-[#1A2C22] font-medium">
                          Additional Details
                        </Label>
                        <Textarea
                          id="message"
                          name="message"
                          data-testid="contact-input-message"
                          value={formData.message}
                          onChange={handleInputChange}
                          placeholder="Tell us about your landscaping project..."
                          className="mt-2 min-h-[120px] bg-[#FAFAF8] border-gray-200"
                        />
                      </div>

                      <Button
                        type="submit"
                        data-testid="contact-submit"
                        disabled={isSubmitting}
                        className="w-full bg-[#C2410C] hover:bg-[#9A3412] text-white h-14 text-lg rounded-full"
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            Submitting...
                          </>
                        ) : (
                          <>
                            <Send className="w-5 h-5 mr-2" />
                            Get My Free Estimate
                          </>
                        )}
                      </Button>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ContactPage;
